import { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  IconButton,
  Popover,
  Box,
  CircularProgress,
} from '@mui/material';
import { Visibility as VisibilityIcon, DescriptionOutlined as DescriptionOutlinedIcon } from '@mui/icons-material';

import {
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
  LoadingSkeleton,
  NoDataSkeleton,
  CustomButton,
} from '../../../common/components';
import { formatCurrency, formatBankName } from '../../bank-movements/utils/formatters';
import { formatDate } from '../../../utils/formatUtils';
import apiClient from '../../../hooks/apiClient';
import { useExcelExport } from '../../../statistics/habitat-99/hooks/useExcelExport';

const ProjectedReportTable = ({ report, isLoading, bankColumns: bankColumnsProp }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const { exportToExcel } = useExcelExport();

  const periods = useMemo(() => report?.periods || [], [report]);

  const bankColumns = useMemo(() => {
    if (bankColumnsProp && bankColumnsProp.length) {
      return bankColumnsProp;
    }

    const bankSet = new Set();
    periods.forEach((period) => {
      period.banks?.forEach((bank) => {
        bankSet.add(bank.bank_name);
      });
    });
    return Array.from(bankSet).sort();
  }, [bankColumnsProp, periods]);

  const handleViewMovements = async (event, period, bankName) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPeriod({ period, bankName });
    setLoadingMovements(true);
    setMovements([]);

    try {
      const response = await apiClient.get('bank_projected_reports/period-movements', {
        params: {
          start_date: period.start_date,
          end_date: period.end_date,
          bank_name: bankName,
        },
      });
      setMovements(response.data || []);
    } catch (error) {
      console.error('Error fetching period movements:', error);
      setMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedPeriod(null);
    setMovements([]);
  };

  const handleExportMovements = () => {
    if (!movements.length) return;

    const rows = movements.map((mov) => ({
      Fecha: formatDate(mov.emission_date),
      Tipo: mov.movement_type_name,
      Concepto: mov.concept || '-',
      Beneficiario: mov.legal_entity_name || '-',
      Importe: mov.amount || 0,
      Conciliado: mov.is_reconciled ? 'Sí' : 'No',
    }));

    exportToExcel(rows, 'MovimientosPeriodo');
  };

  const open = Boolean(anchorEl);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <GeneralTableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <HeadTableCell sx={{ minWidth: 220 }}>Periodo</HeadTableCell>
              {bankColumns.map((bankName) => (
                <HeadTableCell key={bankName} sx={{ minWidth: 140, textAlign: 'right' }}>
                  {formatBankName(bankName)}
                </HeadTableCell>
              ))}
              <HeadTableCell sx={{ minWidth: 140, textAlign: 'right' }}>Total neto</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {periods && periods.length > 0 ? (
              periods.map((period) => {
                const bankSnapshots = Object.fromEntries(
                  (period.banks || []).map((bank) => [bank.bank_name, bank])
                );

                return (
                  <TableRow key={period.period_index} hover>
                    <RowTableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {period.label ||
                          `${formatDate(period.start_date)} al ${formatDate(period.end_date)}`}
                      </Typography>
                    </RowTableCell>

                    {bankColumns.map((bankName) => {
                      const bankData = bankSnapshots[bankName];
                      const net = bankData?.net_movement || 0;
                      const closing = bankData?.closing_balance || 0;

                      return (
                        <RowTableCell
                          key={`${period.period_index}-${bankName}`}
                          sx={{ textAlign: 'right' }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Box>
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                                fontWeight="medium"
                                color={net >= 0 ? theme.palette.success.main : theme.palette.error.main}
                              >
                                {formatCurrency(net, 2)}
                              </Typography>
                              <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                                Libro: {formatCurrency(closing, 2)}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={(e) => handleViewMovements(e, period, bankName)}
                              sx={{ p: 0.5 }}
                              title="Ver movimientos del periodo"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </RowTableCell>
                      );
                    })}

                    <RowTableCell sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight="bold"
                        color={
                          period.totals?.net_movement >= 0
                            ? theme.palette.success.main
                            : theme.palette.error.main
                        }
                      >
                        {formatCurrency(period.totals?.net_movement || 0, 2)}
                      </Typography>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                        Cierre: {formatCurrency(period.totals?.closing_balance || 0, 2)}
                      </Typography>
                    </RowTableCell>
                  </TableRow>
                );
              })
            ) : (
              <NoDataSkeleton
                message="No hay información proyectada para mostrar."
                showSubtitle={false}
              />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 500, maxWidth: 800 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Movimientos del periodo - {selectedPeriod && formatBankName(selectedPeriod.bankName)}
            </Typography>
            <CustomButton
              variant="base"
              size="small"
              onClick={handleExportMovements}
              disabled={!movements.length}
              startIcon={<DescriptionOutlinedIcon fontSize="small" />}
            >
              Descargar XLSX
            </CustomButton>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {selectedPeriod && `${formatDate(selectedPeriod.period.start_date)} al ${formatDate(selectedPeriod.period.end_date)}`}
          </Typography>

          {loadingMovements ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : movements.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <HeadTableCell>Fecha</HeadTableCell>
                    <HeadTableCell>Tipo</HeadTableCell>
                    <HeadTableCell>Concepto</HeadTableCell>
                    <HeadTableCell>Beneficiario</HeadTableCell>
                    <HeadTableCell>Conciliado</HeadTableCell>
                    <HeadTableCell sx={{ textAlign: 'right' }}>Importe</HeadTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map((mov) => (
                    <TableRow key={mov.uuid}>
                      <RowTableCell>
                        <Typography variant="caption">
                          {formatDate(mov.emission_date)}
                        </Typography>
                      </RowTableCell>
                      <RowTableCell>
                        <Typography variant="caption">{mov.movement_type_name}</Typography>
                      </RowTableCell>
                      <RowTableCell>
                        <Typography variant="caption" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {mov.concept || '-'}
                        </Typography>
                      </RowTableCell>
                      <RowTableCell>
                        <Typography variant="caption">{mov.legal_entity_name || '-'}</Typography>
                      </RowTableCell>
                      <RowTableCell>
                        <Typography variant="caption">
                          {mov.is_reconciled ? 'Sí' : 'No'}
                        </Typography>
                      </RowTableCell>
                      <RowTableCell sx={{ textAlign: 'right' }}>
                        <Typography
                          variant="caption"
                          fontFamily="monospace"
                          fontWeight="medium"
                          color={mov.amount >= 0 ? theme.palette.success.main : theme.palette.error.main}
                        >
                          {formatCurrency(mov.amount, 2)}
                        </Typography>
                      </RowTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No hay movimientos registrados para este periodo.
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ProjectedReportTable;
